import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { RoleAssignmentForm } from "@/components/roles/RoleAssignmentForm";
import {
  UserInfoForm,
  UserInfoFormValues,
} from "@/components/users/UserInfoForm";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import { userApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";

const UserEdit = () => {
  const { id } = useParams<{ id: string }>();
  const userId = parseInt(id || "0");
  const { toast } = useToast();
  const navigate = useNavigate();
  const { selectedRole } = useAuth();

  const [roleAssignments, setRoleAssignments] = React.useState<{
    platform: number[];
    management_company: Record<string, number[]>;
    association: Record<string, number[]>;
  }>({
    platform: [],
    management_company: {},
    association: {},
  });

  const [userInfo, setUserInfo] = React.useState<UserInfoFormValues | null>(
    null
  );
  const [isLoadingRoles, setIsLoadingRoles] = React.useState(true);

  const { data: user, isLoading } = useQuery({
    queryKey: ["user", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) throw error;
      return data;
    },
  });

  React.useEffect(() => {
    const fetchUserRoles = async () => {
      setIsLoadingRoles(true);
      try {
        // Fetch platform roles
        const { data: platformRoles, error: platformError } = await supabase
          .from("platform_role_assignments")
          .select("role_id")
          .eq("user_id", userId);

        if (platformError) throw platformError;

        // Fetch management company roles
        const { data: mcRoles, error: mcError } = await supabase
          .from("management_company_role_assignments")
          .select("management_company_id, role_id")
          .eq("user_id", userId);

        if (mcError) throw mcError;

        // Fetch association roles
        const { data: assocRoles, error: assocError } = await supabase
          .from("association_role_assignments")
          .select("association_id, role_id")
          .eq("user_id", userId);

        if (assocError) throw assocError;

        // Process and set role assignments
        const mcRolesMap: Record<string, number[]> = {};
        const assocRolesMap: Record<string, number[]> = {};

        mcRoles?.forEach((role) => {
          const mcId = role.management_company_id.toString();
          mcRolesMap[mcId] = mcRolesMap[mcId] || [];
          mcRolesMap[mcId].push(role.role_id);
        });

        assocRoles?.forEach((role) => {
          const assocId = role.association_id.toString();
          assocRolesMap[assocId] = assocRolesMap[assocId] || [];
          assocRolesMap[assocId].push(role.role_id);
        });

        setRoleAssignments({
          platform: platformRoles?.map((role) => role.role_id) || [],
          management_company: mcRolesMap,
          association: assocRolesMap,
        });
      } catch (error) {
        console.error("Error fetching user roles:", error);
        toast({
          title: "Error",
          description: "Failed to load user roles",
          variant: "destructive",
        });
      } finally {
        setIsLoadingRoles(false);
      }
    };

    if (userId) {
      fetchUserRoles();
    }
  }, [userId, toast]);

  const handleRoleAssigned = (
    roleId: number,
    entityType: "platform" | "management_company" | "association",
    entityId?: number
  ) => {
    setRoleAssignments((prev) => {
      if (entityType === "platform") {
        return {
          ...prev,
          platform: [...prev.platform, roleId],
        };
      } else if (entityId) {
        const entityKey =
          entityType === "management_company"
            ? "management_company"
            : "association";
        const entityIdStr = entityId.toString();
        return {
          ...prev,
          [entityKey]: {
            ...prev[entityKey],
            [entityIdStr]: [...(prev[entityKey][entityIdStr] || []), roleId],
          },
        };
      }
      return prev;
    });
  };

  const handleRoleRemoved = (
    roleId: number,
    entityType: "platform" | "management_company" | "association",
    entityId?: number
  ) => {
    setRoleAssignments((prev) => {
      if (entityType === "platform") {
        return {
          ...prev,
          platform: prev.platform.filter((id) => id !== roleId),
        };
      } else if (entityId) {
        const entityKey =
          entityType === "management_company"
            ? "management_company"
            : "association";
        const entityIdStr = entityId.toString();
        return {
          ...prev,
          [entityKey]: {
            ...prev[entityKey],
            [entityIdStr]: (prev[entityKey][entityIdStr] || []).filter(
              (id) => id !== roleId
            ),
          },
        };
      }
      return prev;
    });
  };

  const handleUserInfoChange = (values: UserInfoFormValues) => {
    setUserInfo(values);
  };

  const handleSaveAll = async () => {
    try {
      // Prepare user info for update
      const userInfoUpdate = userInfo
        ? {
            first_name: userInfo.first_name,
            last_name: userInfo.last_name,
            email: userInfo.email,
            role: userInfo.role,
            job_title: userInfo.job_title,
            phone_number: userInfo.phone_number,
          }
        : undefined;

      // Use the new bulk update API
      await userApi.bulkUpdate(userId, userInfoUpdate, roleAssignments);

      toast({
        title: "Success",
        description: "User information and roles updated successfully",
      });

      navigate("/backoffice/users");
    } catch (error: any) {
      console.error("User update error:", error);
      toast({
        title: "Error",
        description:
          error.message || "Failed to update user information and roles",
        variant: "destructive",
      });
    }
  };

  if (isLoading || !user) {
    return (
      <BackofficeLayout>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </BackofficeLayout>
    );
  }

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => navigate("/backoffice/users")}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Users
          </Button>
          <Button onClick={handleSaveAll}>Save Changes</Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Edit User Information</CardTitle>
          </CardHeader>
          <CardContent>
            <UserInfoForm user={user} onSubmit={handleUserInfoChange} />
          </CardContent>
        </Card>

        {isLoadingRoles ? (
          <div className="flex justify-center items-center h-24">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {selectedRole?.type === "platform" && (
              <Card>
                <CardHeader>
                  <CardTitle>Platform Roles</CardTitle>
                </CardHeader>
                <CardContent>
                  <RoleAssignmentForm
                    userId={userId}
                    entityType="platform"
                    onRoleAssigned={(roleId) =>
                      handleRoleAssigned(roleId, "platform")
                    }
                    onRoleRemoved={(roleId) =>
                      handleRoleRemoved(roleId, "platform")
                    }
                    initialRoles={roleAssignments.platform}
                  />
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Management Company Roles</CardTitle>
              </CardHeader>
              <CardContent>
                <RoleAssignmentForm
                  userId={userId}
                  entityType="management_company"
                  onRoleAssigned={(roleId, entityId) =>
                    handleRoleAssigned(roleId, "management_company", entityId)
                  }
                  onRoleRemoved={(roleId, entityId) =>
                    handleRoleRemoved(roleId, "management_company", entityId)
                  }
                  initialRoles={roleAssignments.management_company}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Association Roles</CardTitle>
              </CardHeader>
              <CardContent>
                <RoleAssignmentForm
                  userId={userId}
                  entityType="association"
                  onRoleAssigned={(roleId, entityId) =>
                    handleRoleAssigned(roleId, "association", entityId)
                  }
                  onRoleRemoved={(roleId, entityId) =>
                    handleRoleRemoved(roleId, "association", entityId)
                  }
                  initialRoles={roleAssignments.association}
                />
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </BackofficeLayout>
  );
};

export default UserEdit;
